import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { getArmBaseUrl } from '@/services/armConfig';
import { listPickerResourceGroups, listPickerResources, listPickerResourceTypes, listPickerSubscriptions } from '@/services/azureResourcePickerService';
import type { AzurePickerPage, AzurePickerScope } from '@/types/azureResourcePicker';

export function useAzureResourcePickerIdentity(): string {
  const { account } = useAuth();
  const tenantId = useAppStore((state) => state.selectedTenantId);
  return JSON.stringify([getArmBaseUrl(), account?.homeAccountId, tenantId ?? account?.tenantId]);
}

function usePickerQuery<T>(key: readonly unknown[], enabled: boolean, fetchPage: (token: string, signal: AbortSignal, continuation?: string) => Promise<AzurePickerPage<T>>) {
  const { getArmToken } = useAuth();
  const identity = useAzureResourcePickerIdentity();
  return useInfiniteQuery({
    queryKey: ['azureResourcePicker', identity, ...key],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ signal, pageParam }) => {
      const token = await getArmToken();
      signal.throwIfAborted();
      if (!token) throw new Error('Sign in to browse Azure resources. You can still enter an ID manually.');
      return fetchPage(token, signal, pageParam);
    },
    getNextPageParam: (page) => page.continuation,
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useAzureResourcePicker(scope: AzurePickerScope, resourcesEnabled: boolean) {
  const canBrowseTypes = resourcesEnabled && !!scope.subscriptionId && !!scope.resourceGroup;
  const canBrowseResources = canBrowseTypes && !!scope.resourceType;
  const subscriptions = usePickerQuery(['subscriptions'], true, listPickerSubscriptions);
  const groups = usePickerQuery(['groups', scope.subscriptionId], !!scope.subscriptionId,
    (token, signal, continuation) => listPickerResourceGroups(token, scope, signal, continuation));
  const types = usePickerQuery(['types', scope.subscriptionId, scope.resourceGroup, scope.allowedResourceTypes], canBrowseTypes,
    (token, signal, continuation) => listPickerResourceTypes(token, scope, signal, continuation));
  const resources = usePickerQuery(['resources', scope], canBrowseResources,
    (token, signal, continuation) => listPickerResources(token, scope, signal, continuation));
  return { subscriptions, groups, types, resources };
}